// Fiche d'un signalement : description, lieu, auteur, photos, doublons rattachés ; qualification par
// le chef de service ou le responsable technique (recevable avec intervention créée, orienté vers
// un organisme, rejeté avec motif, doublon d'un principal), chaque forme avec ses champs.
import { CODES_PRIORITES_INTERVENTION, PRIORITES_INTERVENTION, schemaQualificationSignalement } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Lien, LienBouton, Panneau, Selection, ZoneTexte } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useServices } from '../demarches/contenu';
import { INVALIDER_INTERVENTIONS, qualifierSignalement, useSignalement, useSignalements } from './api';
import { LIBELLES_AUTEUR } from './logique';
import { puceQualification } from './PageSignalements';
import { Photos } from './Photo';
import type { SignalementDetail } from './types';
import commun from '../../commun/commun.module.css';
import styles from './interventions.module.css';

function Qualification({ s }: { s: SignalementDetail }) {
  const services = useServices();
  const candidats = useSignalements({ qualification: 'recevable' });
  const [q, setQ] = useState('recevable');
  const [v, setV] = useState({ service_id: s.service?.id ?? '', priorite: 'normale', titre: '', motif: '', organisme_oriente: '', principal_id: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const brut =
        q === 'recevable'
          ? { qualification: q, service_id: v.service_id || undefined, priorite: v.priorite, titre: v.titre || undefined, motif: v.motif || undefined }
          : q === 'oriente'
            ? { qualification: q, organisme_oriente: v.organisme_oriente, motif: v.motif || undefined }
            : q === 'rejete'
              ? { qualification: q, motif: v.motif }
              : { qualification: q, principal_id: v.principal_id, motif: v.motif || undefined };
      const corps = schemaQualificationSignalement.safeParse(brut);
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return qualifierSignalement(s.id, corps.data);
    },
    succes: (r) => (r.intervention ? `Signalement ${r.numero} recevable : intervention ${r.intervention.numero ?? ''} créée.` : `Signalement ${r.numero} qualifié : ${r.qualification_libelle}.`),
    invalider: INVALIDER_INTERVENTIONS,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Panneau titre="Qualifier le signalement">
      <Selection
        libelle="Qualification"
        options={[
          { valeur: 'recevable', libelle: 'Recevable : une intervention est créée' },
          { valeur: 'oriente', libelle: 'Orienté vers un organisme compétent' },
          { valeur: 'rejete', libelle: 'Rejeté (motif communiqué à l\'auteur)' },
          { valeur: 'doublon', libelle: 'Doublon d\'un signalement existant' },
        ]}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {q === 'recevable' ? (
        <>
          <Selection libelle="Service chargé" optionVide="Service des techniques par défaut" options={(services.data ?? []).map((x) => ({ valeur: x.id, libelle: x.nom }))} value={v.service_id} onChange={(e) => setV({ ...v, service_id: e.target.value })} facultatif />
          <Selection libelle="Priorité" options={CODES_PRIORITES_INTERVENTION.map((p) => ({ valeur: p, libelle: PRIORITES_INTERVENTION[p] }))} value={v.priorite} onChange={(e) => setV({ ...v, priorite: e.target.value })} />
          <Champ libelle="Titre de l'intervention" value={v.titre} onChange={(e) => setV({ ...v, titre: e.target.value })} facultatif aide="Vide : la catégorie et le lieu du signalement" erreur={champ('titre')} />
        </>
      ) : null}
      {q === 'oriente' ? <Champ libelle="Organisme compétent" value={v.organisme_oriente} onChange={(e) => setV({ ...v, organisme_oriente: e.target.value })} erreur={champ('organisme_oriente')} required /> : null}
      {q === 'doublon' ? (
        <Selection libelle="Signalement principal" optionVide="Choisir" options={(candidats.data ?? []).filter((c) => c.id !== s.id).map((c) => ({ valeur: c.id, libelle: `${c.numero} · ${c.categorie_libelle} · ${c.adresse ?? ''}` }))} value={v.principal_id} onChange={(e) => setV({ ...v, principal_id: e.target.value })} erreur={champ('principal_id')} required />
      ) : null}
      <ZoneTexte libelle={q === 'rejete' ? 'Motif (communiqué à l\'auteur)' : 'Commentaire'} value={v.motif} onChange={(e) => setV({ ...v, motif: e.target.value })} erreur={champ('motif')} required={q === 'rejete'} facultatif={q !== 'rejete'} rows={3} />
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      <div className={styles.actionsFiche}>
        <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>
          Enregistrer la qualification
        </Bouton>
      </div>
    </Panneau>
  );
}

export function PageSignalement() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const fiche = useSignalement(id);
  const s = fiche.data;
  return (
    <>
      <EnTetePage
        etiquette={s ? `Signalement, ${s.categorie_libelle}` : 'Signalement'}
        titre={s ? `Signalement ${s.numero}` : 'Signalement'}
        description={s ? <span className={commun.ligne}>{puceQualification(s)}<span>Statut vu par l&apos;usager : {s.statut_usager.libelle}</span></span> : null}
        actions={
          <>
            <LienBouton href="/interventions/signalements" icone={ArrowLeft}>
              Signalements
            </LienBouton>
            {s?.intervention ? <LienBouton href={`/interventions/${s.intervention.id}`} variante="principal">Intervention {s.intervention.numero ?? ''}</LienBouton> : null}
          </>
        }
      />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L3-A" fonction="La fiche du signalement">
        {s ? (
          <div className={commun.deuxColonnes}>
            <div className={commun.pile}>
              <Panneau titre="Signalement">
                <p className={styles.description}>{s.description}</p>
                <dl className={commun.definitions}>
                  <dt>Lieu</dt>
                  <dd>{s.adresse ?? 'non renseigné'}{s.position ? ` (${s.position.latitude.toFixed(5)}, ${s.position.longitude.toFixed(5)})` : ''}</dd>
                  <dt>Origine</dt>
                  <dd>{LIBELLES_AUTEUR[s.auteur.type] ?? s.auteur.type}{s.auteur.nom ? `, ${s.auteur.nom}` : ''}{s.auteur.telephone ? `, ${s.auteur.telephone}` : ''}</dd>
                  <dt>Reçu le</dt>
                  <dd>{dateHeure(s.cree_le)}</dd>
                  <dt>Service</dt>
                  <dd>{s.service?.nom ?? 'non affecté'}</dd>
                  {s.qualifie_le ? (
                    <>
                      <dt>Qualifié le</dt>
                      <dd>{dateHeure(s.qualifie_le)}</dd>
                    </>
                  ) : null}
                  {s.organisme_oriente ? (
                    <>
                      <dt>Orienté vers</dt>
                      <dd>{s.organisme_oriente}</dd>
                    </>
                  ) : null}
                  {s.motif ? (
                    <>
                      <dt>Motif</dt>
                      <dd>{s.motif}</dd>
                    </>
                  ) : null}
                  {s.principal ? (
                    <>
                      <dt>Doublon de</dt>
                      <dd>
                        <Lien href={`/interventions/signalements/${s.principal.id}`}>{s.principal.numero ?? 'signalement principal'}</Lien>
                      </dd>
                    </>
                  ) : null}
                </dl>
              </Panneau>
              <Panneau titre="Photos">
                <Photos photos={s.photos} vide="Aucune photo jointe." />
              </Panneau>
              {s.doublons_liste.length > 0 ? (
                <Panneau titre={`Doublons rattachés (${s.doublons_liste.length})`}>
                  <ul>
                    {s.doublons_liste.map((d) => (
                      <li key={d.id}>
                        <Lien href={`/interventions/signalements/${d.id}`}>{d.numero}</Lien> · {dateHeure(d.cree_le)} · {d.description.slice(0, 80)}
                        {d.au_compte ? ' · auteur notifié par ce signalement' : ''}
                      </li>
                    ))}
                  </ul>
                </Panneau>
              ) : null}
            </div>
            <div className={commun.pile}>
              {s.qualification === 'a_qualifier' && peut(contexte, 'intervention', 'modifier') ? <Qualification s={s} /> : null}
              {s.qualification === 'a_qualifier' && !peut(contexte, 'intervention', 'modifier') ? <Bandeau type="info">La qualification est réservée au chef de service et au responsable technique.</Bandeau> : null}
            </div>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
